// permissions.js - Frontend utility for handling permissions

/**
 * Check if the user has a specific permission
 * @param {Object} user - User object from auth context
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether user has the permission
 */
export const hasPermission = (user, permission) => {
  if (!user) {
    return false;
  }

  // Handle case where permissions might be in different formats
  const permissions = user.permissions || user.role_permissions || {};

  return permissions[permission] === true;
};

/**
 * Check if the user is an admin
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user has admin privileges
 */
export const isAdmin = (user) => {
  if (!user) return false;

  // Check for role property first (common in impersonated users)
  if (user.role === 'Admin') return true;

  return hasPermission(user, 'full_admin');
};

/**
 * Check if the user is a super admin
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user is a super admin
 */
/**
 * Whether the user sees the Triggers page (its Moments): super admins always; account admins when the account's
 * enable-v3-ui-perf-grid setting is on. One rule for the sidebar entry and the Analyst Overview's download.
 */
export const canSeeTriggers = (user, accountSettings) => isSuperAdmin(user)
  || (isAdmin(user) && accountSettings?.['enable-v3-ui-perf-grid'] === 'true');

export const isSuperAdmin = (user) => {
  if (!user) return false;

  // NOTE: nothing in this app currently sets `user.isSuperAdmin` — the
  // impersonate route returns the TARGET user's permissions, so this call
  // deliberately goes FALSE during impersonation and the impersonated session
  // gets the impersonated user's access (no Admin page, no account switcher).
  // For the few powers a super admin should KEEP while impersonating, check
  // `user.isImpersonating` alongside this (see CampaignDetailPage).
  if (user.isSuperAdmin === true) return true;

  return hasPermission(user, 'impersonate_admin');
};

/**
 * Check if the user can send messages
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user can send messages
 */
export const canSendMessage = (user) => {
  return hasPermission(user, 'can_send_message');
};

/**
 * Check if the user can upload attachments
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user can upload attachments
 */
export const canUploadAttachment = (user) => {
  return hasPermission(user, 'can_upload_attachment');
};

/**
 * Check if the user can manage chat history
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user can manage chat history
 */
export const canManageChatHistory = (user) => {
  return hasPermission(user, 'can_manage_chat_hisotry');
};

/**
 * Check if the user can regenerate responses
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user can regenerate responses
 */
export const canRegenerate = (user) => {
  return hasPermission(user, 'can_regenerate');
};

/**
 * Check if the user can ask the assistant
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user can ask the assistant
 */
export const canAskAssistant = (user) => {
  return hasPermission(user, 'can_ask_assistant');
};

/**
 * Check if the user can export to Google Sheets
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user can export to Google Sheets
 */
export const canExportToGSheets = (user) => {
  return hasPermission(user, 'can_export_gsheet');
};

/**
 * Check if the user can approve messages
 * @param {Object} user - User object from auth context
 * @returns {boolean} - Whether user can approve messages
 */
export const canApprove = (user) => {
  return hasPermission(user, 'can_approve');
};

/**
 * Static mapping from platform/destination aliases to canonical channel names
 * This handles cases where platform names differ from channel_type names.
 *
 * SCOPE: permission-key generation only (channel_<name>_access). Parsing
 * platform-list ACCOUNT SETTINGS uses the alias map in
 * utils/accountSettingPlatformList.js instead — that one is broader (it also
 * folds 'meta' and 'google-ad-manager'), and folding those here would change
 * which permission key a channel resolves to.
 */
const PLATFORM_ALIASES = {
  'google_search': 'google-ads-search',
  'google_pmax': 'google-ads-pmax',
  'google_demandgen': 'google-ads-demandgen',
  'google_display': 'google-ads-display',
  'g-ads-search': 'google-ads-search',
  'g-ads-pmax': 'google-ads-pmax',
  'g-ads-demandgen': 'google-ads-demandgen',
  'demandgen': 'google-ads-demandgen',
  'search': 'google-ads-search',
  'fb': 'facebook',
  'amazon_dsp': 'amazon-dsp',
  'yahoo_dsp': 'yahoo',
  'yahoo-dsp': 'yahoo',
  // The create form's destination id is 'gam'; the channel type is spelled out.
  'gam': 'google-ad-manager',
  'gam_api': 'google-ad-manager',
  'gam-api': 'google-ad-manager',
  'google_ad_manager': 'google-ad-manager',
};

/**
 * Generate channel permission key from platform name
 * @param {string} platform - Platform/destination name
 * @returns {string} - Permission key in format: channel_{name}_access
 */
export const generateChannelPermissionKey = (platform) => {
  if (!platform) return null;
  // Normalize: replace hyphens with underscores and convert to lowercase
  const normalized = platform.toLowerCase().replace(/-/g, '_');
  return `channel_${normalized}_access`;
};

/**
 * Get the channel permission key for a given platform
 * Handles platform aliases and generates permission key dynamically
 * @param {string} platform - Platform/destination name
 * @returns {string|null} - Permission key or null if not found
 */
export const getChannelPermissionKey = (platform) => {
  if (!platform) return null;
  const normalizedPlatform = platform.toLowerCase();

  // Check if there's an alias mapping
  const canonicalName = PLATFORM_ALIASES[normalizedPlatform] || normalizedPlatform;

  // Generate permission key from canonical name
  return generateChannelPermissionKey(canonicalName);
};

/**
 * Check if user has access to a specific channel/platform
 * @param {Object} user - User object from auth context
 * @param {string} platform - Platform/destination name
 * @returns {boolean} - Whether user has channel access
 */
export const hasChannelAccess = (user, platform) => {
  if (!user) return false;

  // Super Admin has access to all channels
  if (isSuperAdmin(user)) return true;

  const permissionKey = getChannelPermissionKey(platform);
  if (!permissionKey) {
    // If no permission key found for platform, allow access (legacy behavior)
    console.warn(`No channel permission mapping found for platform: ${platform}`);
    return true;
  }
  return hasPermission(user, permissionKey);
};

/**
 * Get all channels a user has access to based on their permissions
 * NOTE: For dynamic channel list, use the API endpoint /api/user/accessible-channels/:accountId
 * @param {Object} user - User object from auth context
 * @param {Object} accessibleChannelsFromAPI - Optional: accessible channels from API response
 * @returns {string[]} - Array of platform names user can access
 */
export const getUserAccessibleChannels = (user, accessibleChannelsFromAPI = null) => {
  if (!user) return [];

  // Super Admin has access to all channels
  if (isSuperAdmin(user)) {
    return null; // null means all channels
  }

  // If we have API response, use it
  if (accessibleChannelsFromAPI) {
    return Object.keys(accessibleChannelsFromAPI).filter(key => accessibleChannelsFromAPI[key]);
  }

  // Fallback: check permissions object for channel permissions
  const permissions = user.permissions || user.role_permissions || {};
  const accessibleChannels = [];

  for (const [key, value] of Object.entries(permissions)) {
    if (key.startsWith('channel_') && key.endsWith('_access') && value === true) {
      // Extract channel name from permission key: channel_facebook_access -> facebook
      const channelName = key.replace('channel_', '').replace('_access', '').replace(/_/g, '-');
      accessibleChannels.push(channelName);
    }
  }

  return accessibleChannels;
};

/**
 * Filter campaigns based on user's channel access
 * @param {Object} user - User object from auth context
 * @param {Array} campaigns - Array of campaign objects
 * @returns {Array} - Filtered campaigns user can access
 */
export const filterCampaignsByChannelAccess = (user, campaigns) => {
  if (!user || !campaigns) return [];

  // Super Admin sees all campaigns
  if (isSuperAdmin(user)) return campaigns;

  return campaigns.filter(campaign => {
    const platform = campaign.destination || campaign.platform;
    return hasChannelAccess(user, platform);
  });
};